import React from "react";
import { useLang } from "@/contexts/LanguageContext";
import { Zap, Scale, TrendingUp, CheckCircle2, Star, Circle } from "lucide-react";
import { marketplaceName } from "@/lib/marketplaces";
import { displayUsedPrice } from "@/lib/price";

const STRATEGY_META = {
  fast:       { bg: "bg-[#002FA7]", text: "text-white", icon: <Zap /> },
  balanced:   { bg: "bg-[#FFCC00]", text: "text-black", icon: <Scale /> },
  max_profit: { bg: "bg-[#FF3B30]", text: "text-white", icon: <TrendingUp /> },
};

export const StrategyCard = ({ strategy, index, recommended, selected, onSelect, marketplaces = [] }) => {
  const { t } = useLang();
  const meta = STRATEGY_META[strategy.goal];
  const isRecommended = strategy.goal === recommended;

  return (
    <div
      data-testid={`strategy-${strategy.goal}`}
      role="button"
      tabIndex={0}
      onClick={onSelect}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onSelect(); } }}
      className={`lg:col-span-4 border bg-white cursor-pointer transition-all hover:-translate-y-1 flex flex-col ${selected ? "border-2 border-[#002FA7] shadow-hard-blue" : isRecommended ? "border-2 border-black shadow-hard" : "border-black"}`}
    >
      <div className={`${meta.bg} ${meta.text} p-6 flex items-center justify-between`}>
        <div className="flex items-center gap-2">
          {meta.icon}
          <span className="font-mono font-bold text-xs tracking-widest uppercase">{`0${index + 1} / ${strategy.goal.replace("_", " ")}`}</span>
        </div>
        {isRecommended && (
          <span data-testid="recommended-badge" className="flex items-center gap-1 bg-black text-white font-mono text-[9px] font-bold tracking-widest uppercase px-2 py-1">
            <Star size={10} /> {t.results.recommended}
          </span>
        )}
      </div>
      <div className="p-6 flex-1">
        <div className="font-display font-black text-2xl tracking-tight">{strategy.label}</div>
        <div className="font-display font-black text-5xl tracking-tighter mt-3">
          €{displayUsedPrice(strategy.price)}
        </div>
        <p className="mt-4 text-sm text-[#4A4A4A] leading-relaxed">{strategy.description}</p>

        <div className="mt-6 border-t border-black/10 pt-4">
          <div className="overline text-[#737373]">{t.results.timeline}</div>
          <div className="font-mono font-bold mt-1">{strategy.timeline}</div>
        </div>

        <div className="mt-4">
          <div className="overline text-[#737373]">{t.results.channels}</div>
          <div className="flex flex-wrap gap-2 mt-2">
            {marketplaces.map((m) => (
              <span key={m} data-testid={`channel-${m}`} className="border border-black px-2 py-1 font-mono text-xs font-bold">{marketplaceName(m)}</span>
            ))}
          </div>
        </div>

        <div className="mt-4">
          <div className="overline text-[#737373]">{t.results.tips}</div>
          <ul className="mt-2 space-y-2">
            {strategy.tips.map((tip) => (
              <li key={tip} className="text-sm leading-snug flex gap-2">
                <CheckCircle2 size={14} className="text-[#002FA7] mt-0.5 flex-shrink-0" />
                <span>{tip}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
      <div
        data-testid={`strategy-select-${strategy.goal}`}
        className={`px-6 py-3.5 border-t font-mono text-[10px] font-bold tracking-widest uppercase flex items-center gap-2 ${selected ? "bg-[#002FA7] text-white border-[#002FA7]" : "bg-[#F5F5F5] text-[#4A4A4A] border-black/10"}`}
      >
        {selected ? <CheckCircle2 size={14} /> : <Circle size={14} />}
        {selected ? t.results.price_selected : t.results.tap_to_select}
      </div>
    </div>
  );
};

export default StrategyCard;
